import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { MapPin, Users, Video, Globe, Clock, Briefcase, Heart } from "lucide-react"
import { Link } from "react-router-dom"
import { Opportunity } from "@/hooks/useOpportunitiesApi"
import { cn } from "@/lib/utils"

// Function to generate a consistent color based on a string
function stringToColor(str: string) {
  const colors = [
    "from-pink-500 to-rose-500",
    "from-purple-500 to-indigo-500",
    "from-blue-500 to-cyan-500",
    "from-emerald-500 to-green-500",
    "from-amber-500 to-yellow-500",
    "from-orange-500 to-red-500",
    "from-fuchsia-500 to-pink-500",
    "from-sky-500 to-blue-500",
    "from-teal-500 to-emerald-500",
    "from-lime-500 to-green-500",
  ];
  
  // Simple hash function to get a consistent index
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  const index = Math.abs(hash) % colors.length;
  return colors[index];
}

interface OpportunityCardNewProps {
  opportunity: Opportunity
}

export function OpportunityCardNew({ opportunity }: OpportunityCardNewProps) {
  const gradientClass = stringToColor(opportunity.name);

  const getFormatIcon = (format: string) => {
    switch (format) {
      case 'In_Office':
        return <Briefcase className="h-4 w-4" />
      case 'Virtual':
        return <Video className="h-4 w-4" />
      case 'Outdoor':
        return <Globe className="h-4 w-4" />
      default:
        return <Users className="h-4 w-4" />
    }
  }

  const getVolunteerSupportIcon = (support: string) => {
    switch (support) {
      case 'Create':
        return <Heart className="h-4 w-4" />
      case 'Assemble/Craft':
        return <Briefcase className="h-4 w-4" />
      case 'Beneficiary Interaction':
        return <Users className="h-4 w-4" />
      case 'Partcipate':
        return <Users className="h-4 w-4" />
      case 'Learn':
        return <Clock className="h-4 w-4" />
      default:
        return <Heart className="h-4 w-4" />
    }
  }

  return (
    <Link to={`/opportunities/${opportunity.id}`}>
      <Card 
        className="cursor-pointer hover:shadow-lg transition-shadow h-[28rem] flex flex-col rounded-xl overflow-hidden"
      >
        <div className="aspect-video w-full overflow-hidden relative rounded-t-xl">
          {opportunity.image_url ? (
            <img
              src={opportunity.image_url}
              alt={opportunity.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div 
              className={cn(
                "w-full h-full flex items-center justify-center",
                "bg-gradient-to-br", gradientClass
              )}
            >
              <span className="text-white text-3xl font-medium">
                {opportunity.name.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
        </div>
        <CardHeader className="pb-2 pt-4">
          <CardTitle className="text-lg line-clamp-2">{opportunity.name}</CardTitle>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col">
          <p className="text-sm text-gray-600 line-clamp-3 mb-4">
            {opportunity.community_need}
          </p>
          <div className="mt-auto space-y-2">
            {opportunity.location && (
              <div className="flex items-center text-sm">
                <MapPin className="h-4 w-4 mr-2 text-gray-500" />
                <span className="text-gray-600 line-clamp-1">{opportunity.location}</span>
              </div>
            )}
            <div className="flex items-center text-sm">
              {getFormatIcon(opportunity.format)}
              <span className="ml-2 text-gray-600">{opportunity.format.replace('_', ' ')}</span>
            </div>
            <div className="flex items-center text-sm">
              {getVolunteerSupportIcon(opportunity.volunteer_support)}
              <span className="ml-2 text-gray-600 line-clamp-1">{opportunity.volunteer_support}</span>
            </div>
            <div className="flex items-center text-sm">
              <Heart className="h-4 w-4 mr-2 text-gray-500" />
              <span className="text-gray-600 line-clamp-1">{opportunity.focus_area.replace('_', ' & ')}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  )
}
